import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertOctagon, CheckCircle, ChevronRight, Link as LinkIcon, Mail, QrCode, ShieldCheck, TriangleAlert } from 'lucide-react';
import Header from '../components/layout/Header';
import VerdictBadge from '../components/analysis/VerdictBadge';
import { useAuth } from '../components/auth/AuthContext';
import { api } from '../services/api';

// Composants Dynamiques
import AnimatedAlerts from '../components/home/AnimatedAlerts';
import LiveStats from '../components/home/LiveStats';
import QuizSwipe from '../components/home/QuizSwipe';

const actions = [
  { icon: LinkIcon, bg: 'bg-blue-100', color: 'text-accent-blue', label: 'Vérifier un lien', sub: 'Analyse de sites web suspects', to: '/analyze/url' },
  { icon: Mail, bg: 'bg-amber-100', color: 'text-amber-500', label: 'Vérifier un message', sub: "Email, SMS, WhatsApp... détection d'arnaques", to: '/analyze/message' },
  { icon: QrCode, bg: 'bg-gray-100', color: 'text-brand-navy', label: 'Scanner un QR code', sub: 'Vérification de codes physiques', to: '/analyze/qr' },
];

const tips = [
  { icon: ShieldCheck, title: "Vos données, vos droits", text: "La loi sur le numérique vous protège contre l'usurpation d'identité et le vol de données. Vous avez le droit au respect de votre vie privée.", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100" },
  { icon: TriangleAlert, title: "Attention au Phishing", text: "Ne donnez jamais votre code PIN, OTP ou vos mots de passe, même à quelqu'un se faisant passer pour votre banque ou un opérateur.", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-100" },
  { icon: AlertOctagon, title: "Signaler pour protéger", text: "Un signalement aide non seulement à vous protéger, mais aussi à bloquer les cybercriminels et prévenir d'autres victimes.", color: "text-accent-blue", bg: "bg-blue-50", border: "border-blue-100" }
];

function ActionCard({ item, index }) {
  const Icon = item.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      whileHover={{ scale: 1.02, rotateY: 5, rotateX: -5 }}
      whileTap={{ scale: 0.98 }}
      className="perspective-[1000px] h-full"
    >
      <Link to={item.to} className="h-20 md:h-36 bg-white rounded-[14px] px-4 flex md:flex-col md:items-start items-center gap-4 shadow-sm border border-gray-100" aria-label={item.label}>
        <span className={`w-11 h-11 rounded-xl ${item.bg} ${item.color} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-semibold text-sm text-text-primary">{item.label}</span>
          <span className="block text-xs text-text-muted mt-1 leading-snug">{item.sub}</span>
        </span>
        <ChevronRight className="w-5 h-5 text-gray-400 md:hidden" />
      </Link>
    </motion.div>
  );
}

function RecentAnalysisRow({ item, onOpen, index }) {
  const colors = { danger: 'border-l-verdict-danger', suspect: 'border-l-verdict-suspect', safe: 'border-l-verdict-safe' };
  const Icon = item.verdict === 'danger' ? AlertOctagon : item.verdict === 'suspect' ? TriangleAlert : CheckCircle;
  const kind = item.type === 'url' ? 'Lien' : item.type === 'email' || item.type === 'message' ? 'Message' : 'QR Code';

  return (
    <motion.button 
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.2 + index * 0.1, duration: 0.4 }}
      whileHover={{ scale: 1.01, x: 5 }}
      whileTap={{ scale: 0.99 }}
      type="button" 
      onClick={() => onOpen(item)} 
      className={`w-full text-left bg-white rounded-xl border border-gray-100 border-l-4 ${colors[item.verdict]} p-4 flex items-center gap-3 shadow-sm`} 
      aria-label={`Ouvrir l'analyse ${item.label}`}
    >
      <span className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-brand-navy flex-shrink-0">
        <Icon className="w-5 h-5" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-bold text-text-primary truncate">{item.label}</span>
        <span className="block text-sm text-text-subtle mt-0.5">{item.time} · {kind}</span>
      </span>
      <VerdictBadge verdict={item.verdict} size="sm" />
    </motion.button>
  );
}

function TipCard({ item, index }) {
  const Icon = item.icon;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      whileHover={{ y: -5 }}
      className={`bg-white rounded-[14px] p-4 flex gap-4 shadow-sm border ${item.border}`}
    >
      <span className={`w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center ${item.bg} ${item.color}`}>
        <Icon className="w-5 h-5" strokeWidth={2.5} />
      </span>
      <div>
        <h3 className="font-bold text-sm text-brand-navy">{item.title}</h3>
        <p className="text-xs text-text-muted mt-1 leading-relaxed">{item.text}</p>
      </div>
    </motion.div>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();
  const firstName = user?.name?.split(' ')[0] || 'Kofi';

  useEffect(() => {
    let mounted = true;
    api.getHistory().then((items) => { if (mounted) setRecent(items.slice(0, 3)); });
    return () => { mounted = false; };
  }, []);

  const openResult = (item) => navigate('/results', { state: { type: item.type, data: item } });

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="page-container pb-20"
    >
      <Header />
      
      {/* Animated Hero Section */}
      <motion.div 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, type: "spring", bounce: 0.4 }}
        className="mx-4 mt-4 bg-brand-navy rounded-3xl p-5 md:p-8 relative overflow-hidden shadow-lg md:grid md:grid-cols-[1fr_auto] md:items-center md:gap-8"
      >
        <div className="relative z-10">
          <p className="text-white/60 text-sm">Bonjour, {firstName}</p>
          <h1 className="text-white font-bold text-xl md:text-3xl mt-1 leading-snug">Votre sécurité est<br />notre priorité.</h1>
          <p className="text-white/60 text-sm md:text-base mt-2 leading-relaxed">Vérifiez instantanément tout lien ou document suspect pour vous protéger des fraudes.</p>
        </div>
        
        {/* Floating Background Icons */}
        <motion.div 
          animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }}
          transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
          className="absolute -right-4 -top-4 md:static z-0"
        >
          <ShieldCheck className="w-24 h-24 md:w-32 md:h-32 text-white opacity-10 md:opacity-20" strokeWidth={1.8} />
        </motion.div>
        
        {/* Shimmer effect */}
        <motion.div 
          animate={{ x: ['-100%', '200%'] }}
          transition={{ repeat: Infinity, duration: 3, ease: "linear", repeatDelay: 2 }}
          className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 z-0"
        />
      </motion.div>

      {/* Ticker / Animated Alerts */}
      <AnimatedAlerts />

      {/* Action Cards with 3D Tilt */}
      <div className="mx-4 mt-6">
        <p className="font-semibold text-text-primary text-sm mb-3">Actions rapides</p>
        <div className="space-y-3 md:grid md:grid-cols-3 md:gap-3 md:space-y-0">
          {actions.map((item, i) => <ActionCard key={item.label} item={item} index={i} />)}
        </div>
      </div>

      {/* Live Stats animated counter */}
      <LiveStats />

      {/* Interactive Tinder-like swipe quiz */}
      <QuizSwipe />

      {/* Recent History */}
      <div className="mx-4 mt-8">
        <div className="flex justify-between items-center mb-3">
          <p className="font-semibold text-text-primary text-sm">Analyses récentes</p>
          <Link to="/history" className="text-accent-blue text-sm min-h-11 flex items-center">Tout voir</Link>
        </div>
        <div className="space-y-3 md:grid md:grid-cols-3 md:gap-3 md:space-y-0">
          {recent.map((item, i) => <RecentAnalysisRow key={item.id} item={item} onOpen={openResult} index={i} />)}
        </div>
      </div>

      {/* Tips section with Scroll animations */}
      <div className="mx-4 mt-10 mb-8">
        <p className="font-semibold text-text-primary text-sm mb-3">Conseils de Sécurité & Droits</p>
        <div className="space-y-3 md:grid md:grid-cols-3 md:gap-3 md:space-y-0">
          {tips.map((tip, i) => <TipCard key={tip.title} item={tip} index={i} />)}
        </div>
      </div>
    </motion.div>
  );
}
